import React from 'react';
import { Sentry } from '@/utils/logger';

const { ErrorBoundary } = Sentry;

// Match the props structure provided by Sentry ErrorBoundary
interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError(): void;
}

/**
 * Custom error fallback component for Sentry error boundary
 */
const ErrorFallback = ({ 
  error, 
  resetError 
}: ErrorFallbackProps) => {
  // Safely extract error message
  const errorMessage = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
      <div className="mb-6 p-4 rounded-full bg-red-500/10">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
      <p className="text-gray-400 mb-2 max-w-md">
        We've been notified about this issue and are working to fix it.
      </p>
      <p className="text-red-400 text-sm mb-6 max-w-md">
        {errorMessage}
      </p>
      <button
        onClick={resetError}
        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
      >
        Try again
      </button>
    </div>
  );
};

/**
 * Sentry Error Boundary that wraps application components
 * Captures errors and reports them to Sentry automatically
 */
export const SentryErrorBoundary: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <ErrorBoundary 
      fallback={(fallbackProps) => {
        // Cast to the correct type that matches our interface
        return <ErrorFallback 
          error={fallbackProps.error} 
          componentStack={fallbackProps.componentStack || ''}
          eventId={fallbackProps.eventId || ''}
          resetError={fallbackProps.resetError}
        />;
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
